import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';
import { usePageActionsStore } from '@/stores/pageActionsStore';
import { AuthRoutes } from '@/types/enums/RouteNameEnums';
import { authRoutes } from '@/plugins/router/routes/auth';
import { sharedRoutes } from '@/plugins/router/routes/shared';
import { superAdminRoutes } from '@/plugins/router/routes/superAdmin';
import { companyAdminRoutes } from '@/plugins/router/routes/companyAdmin';
import { useAcl } from 'vue-simple-acl';
import { usePostHog } from '@plugins/posthog/usePostHog';

const router = createRouter({
    history: createWebHistory(),
    routes: [...authRoutes, ...sharedRoutes, ...superAdminRoutes, ...companyAdminRoutes],
});

router.beforeEach((to, from, next) => {
    const authStore = useAuthStore();
    authStore.checkAuth();

    // Reset page actions only when changing sections (not child routes)
    const pageActionsStore = usePageActionsStore();
    const toSection = to.meta.navigationKey;
    const fromSection = from.meta.navigationKey;

    if (toSection !== fromSection) {
        pageActionsStore.reset();
    }

    if (to.meta.requiresAuth && !authStore.isAuthenticated) {
        authStore.returnUrl = to.fullPath;
        next({ name: AuthRoutes.Login });

        return;
    }

    // Block integrators from super-admin-only routes
    if (authStore.isAuthenticated && to.meta.superAdminOnly) {
        const isIntegrator = authStore.user?.is_integrator && !authStore.user?.is_super_admin;
        if (isIntegrator) {
            next({ name: AuthRoutes.Login });

            return;
        }
    }

    // Feature check after authentication
    if (authStore.isAuthenticated && to.meta.featureKey && !to.meta.skipFeatureCheck) {
        const { can } = useAcl();
        const isSuperAdmin = authStore.user?.is_super_admin;

        if (!isSuperAdmin && !can(to.meta.featureKey)) {
            to.meta.accessDenied = true;
            next();
        }
    }

    next();
});

usePostHog();

declare module 'vue-router' {
    interface RouteMeta {
        requiresAuth?: boolean;
        pageTitle?: string;
        canAddToFavorite?: boolean;
        navigationKey?: string;
        featureKey?: string;
        hidePageTitle?: boolean;
        skipFeatureCheck?: boolean;
        accessDenied?: boolean;
        superAdminOnly?: boolean;
    }
}

export default router;
